import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { axios } from '../lib/request';
import { useSetRecoilState } from 'recoil';
import { errorState } from '../settings/Settings.state';
import { useTranslation } from 'react-i18next';
import { zonesQueryKey, waypointsQueryKey } from '../lib/refresh';

// Zones query
export const useZonesQuery = () => {
  const { game_id } = useParams();
  return useQuery(
    zonesQueryKey(game_id),
    async () => {
      const { data } = await axios.get(process.env.REACT_APP_API_ZONES_URL, {
        params: { game_id }
      });
      return data;
    }
  );
};

export const useInvalidateZonesQuery = () => {
  const { game_id } = useParams();
  const queryClient = useQueryClient();
  return (newZone) => queryClient.setQueryData(zonesQueryKey(game_id), (old) => {
    const newZones = [...old.zones];
    newZones.push(newZone);
    return {
      ...old,
      zones: newZones,
    };
  });
};

export const useInvalidateWaypointsQuery = (zone_id) => {
  const { game_id } = useParams();
  const queryClient = useQueryClient();
  return (newWaypoint) => queryClient.setQueryData(waypointsQueryKey(game_id, zone_id), (old) => {
    const newWaypoints = [...(old || [])];
    newWaypoints.push(newWaypoint);
    return newWaypoints;
  });
};

// Create zone mutation
export const useCreateZoneMutation = () => {
  const { game_id } = useParams();

  return useMutation(
    async ({ position }) => {
      const { data } = await axios.post(`${process.env.REACT_APP_API_ZONE_CREATE_URL}`, {
        game_id,
        position
      });
      return data?.zone || {};
    },
  );
};

// Update zone mutation
export const useUpdateZoneMutation = () => {
  const { game_id } = useParams();

  return useMutation(
    async ({ id, position }) => {
      const { data } = await axios.put(`${process.env.REACT_APP_API_ZONE_UPDATE_URL}/${id}.json`, {
        game_id,
        position
      });
      return data;
    },
  );
};

// Delete zone mutation
export const useDeleteZoneMutation = () => {
  const { game_id } = useParams();

  return useMutation(
    async (id) => {
      const { data } = await axios.delete(`${process.env.REACT_APP_API_ZONE_DELETE_URL}/${id}.json`, {
        data: { game_id }
      });
      return data;
    },
  );
};

// Create waypoint mutation
export const useCreateWaypointMutation = () => {
  const { t } = useTranslation();
  const { game_id } = useParams();
  const setError = useSetRecoilState(errorState(game_id));

  return useMutation(
    async ({ zone_id, position, latitude, longitude }) => {
      const data = await axios.post(`${process.env.REACT_APP_API_WAYPOINT_CREATE_URL}`, {
        game_id,
        zone_id,
        position,
        latitude,
        longitude
      });
      return data.data;
    },
    {
      onError: (error) => {
        setError(error.response?.data || [t('There was an error creating the waypoint.')]);
        window.scrollTo(0, 0);
      },
    }
  );
};

// Update waypoint mutation
export const useUpdateWaypointMutation = () => {
  const { t } = useTranslation();
  const { game_id } = useParams();
  const setError = useSetRecoilState(errorState(game_id));

  return useMutation(
    async ({ id, zone_id, position, latitude, longitude }) => {
      const { data } = await axios.put(`${process.env.REACT_APP_API_WAYPOINT_UPDATE_URL}/${id}.json`, {
        game_id,
        zone_id,
        position,
        latitude,
        longitude
      });
      return data;
    },
    {
      onError: (error) => {
        setError(error.response?.data || [t('There was an error updating the waypoint.')]);
        window.scrollTo(0, 0);
      },
    }
  );
};

// Delete waypoint mutation
export const useDeleteWaypointMutation = () => {
  const { t } = useTranslation();
  const { game_id } = useParams();
  const setError = useSetRecoilState(errorState(game_id));

  return useMutation(
    async ({ id, zone_id }) => {
      const { data } = await axios.delete(`${process.env.REACT_APP_API_WAYPOINT_DELETE_URL}/${id}.json`, {
        data: { game_id, zone_id }
      });
      return data;
    },
    {
      onError: (error) => {
        setError(error.response?.data || [t('There was an error deleting the waypoint.')]);
        window.scrollTo(0, 0);
      },
    }
  );
};
